import { AppNavItem, Css, Icon, useSideNavLayoutContext } from "@homebound/beam";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getStage } from "src/context";
import { BeamSideNavLayout } from "src/routing/components/layouts/BeamSideNavLayout";
import * as adminPaths from "src/routing/domains/adminPaths";

/**
 * Admin domain chrome: Beam side nav rail + `<Outlet />` for leaf pages.
 *
 * Role: data router pathless layout. Nest under `BeamNavbarLayout` in `domains/admin.tsx`.
 * Stack: `AppShell` → `BeamNavbarLayout` → `AdminSideNavLayout` → page (`PageHeaderLayout`).
 */
export function AdminSideNavLayout() {
  const { pathname } = useLocation();
  const items = useMemo(() => buildAdminNavItems(pathname), [pathname]);

  return <BeamSideNavLayout contrastRail sideNav={{ top: <AdminSideNavHeader />, items }} />;
}

function AdminSideNavHeader() {
  const { navState } = useSideNavLayoutContext();
  if (navState !== "expanded") {
    return null;
  }
  return (
    <div css={Css.pt3.$}>
      <div css={Css.df.jcc.aic.br8.wPx(48).hPx(48).bgGray800.gray50.$}>
        <Icon icon="userCircle" />
      </div>
      <h1 css={Css.lg.mt1.gray100.$}>Admin</h1>
    </div>
  );
}

function buildAdminNavItems(pathname: string): AppNavItem[] {
  const items: AppNavItem[] = [
    {
      section: true,
      label: "Feature Management",
      items: [
        {
          label: "Global",
          onClick: adminPaths.adminFeatureFlagsRoute.url(),
          active: isNavActive(pathname, adminPaths.adminFeatureFlagsRoute.url()),
        },
        {
          label: "Projects",
          onClick: adminPaths.adminProjectsRoute.url(),
          active: isNavActive(pathname, adminPaths.adminProjectsRoute.url()),
        },
      ],
    },
    {
      section: true,
      label: "User Management",
      items: [
        {
          label: "Users",
          onClick: adminPaths.adminUsersRoute.url(),
          active: isNavActive(pathname, adminPaths.adminUsersRoute.url()),
        },
        {
          label: "User Groups",
          onClick: adminPaths.adminUserGroupsRoute.url(),
          active: isNavActive(pathname, adminPaths.adminUserGroupsRoute.url()),
        },
      ],
    },
    {
      section: true,
      label: "Data Management",
      items: [
        {
          label: "Items",
          onClick: adminPaths.adminItemsRoute.url(),
          active: isNavActive(pathname, adminPaths.adminItemsRoute.url()),
        },
        {
          label: "Cost Codes",
          onClick: adminPaths.adminCostCodesRoute.url(),
          active: isNavActive(pathname, adminPaths.adminCostCodesRoute.url()),
        },
        {
          label: "Locations V2",
          onClick: adminPaths.adminLocationsRoute.url(),
          active: isNavActive(pathname, adminPaths.adminLocationsRoute.url()),
        },
        {
          label: "Feature Types",
          onClick: adminPaths.adminFeatureTypesRoute.url(),
          active: isNavActive(pathname, adminPaths.adminFeatureTypesRoute.url()),
        },
      ],
    },
  ];

  if (getStage() === "qa") {
    items.push({
      section: true,
      label: "Infrastructure Management",
      items: [
        {
          label: "QA Database",
          onClick: adminPaths.adminInfrastructureRoute.url(),
          active: isNavActive(pathname, adminPaths.adminInfrastructureRoute.url()),
        },
      ],
    });
  }

  return items;
}

function isNavActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}
